import dayjs from 'dayjs';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ChartHeader from 'components/charts/ChartHeader';
import DataCards from 'components/charts/DataCards';
import AreaChartV2 from 'components/charts/chartJS/AreaChart';
import { DynamicListChartContainer } from 'components/charts/styled/Chart.styled';
import DateFilter from 'components/field/DateFilter';
import Card from 'components/global/Card';
import WidgetPageContainer from 'components/global/WidgetPageContainer';
import WidgetWrapper from 'components/global/WidgetWrapper';
import DynamicList from 'components/list/DynamicList';
import { FilterType } from 'components/list/DynamicListFilterUtils';

import useFetchAndParseOnView from 'hooks/useFetchAndParseOnView';

import { REPORT } from 'services/consts';

import { colorCombinations } from 'utils/colors';
import { DYNAMICLISTS } from 'utils/constants';

const OTOFlexTab = () => (
	<WidgetPageContainer>
		<WidgetWrapper widgetId={'orderValuesSummary'}>
			<OrderValuesSummary />
		</WidgetWrapper>
		<WidgetWrapper widgetId={'numberOfOrders'}>
			<NumberOfOrders />
		</WidgetWrapper>
		<WidgetWrapper widgetId={'driverPerformance'}>
			<DriverPerformance />
		</WidgetWrapper>
	</WidgetPageContainer>
);

const NumberOfOrders = () => {
	const [filter, setFilter] = useState({
		start: dayjs().subtract(7, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end }), [filter]);

	const { t } = useTranslation();

	const afterFetch = useCallback((data) => {
		const labels = [];
		const datasetsMap = {};

		data.forEach((item) => {
			// Add to labels if not already present
			if (!labels.includes(item.xAxis)) {
				labels.push(item.xAxis);
			}

			// Initialize dataset for the type if not already present
			if (!datasetsMap[item.type]) {
				datasetsMap[item.type] = {
					label: t(item.type),
					backgroundColor:
						colorCombinations[Object.keys(datasetsMap).length % colorCombinations.length].color,
					data: []
				};
			}

			// Add yAxis value to the corresponding type dataset
			datasetsMap[item.type].data.push(item.yAxis ? (item.yAxis !== 0 ? item.yAxis : 1) : 0);
		});

		return {
			labels,
			datasets: Object.values(datasetsMap)
		};
	}, []);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.OTO_FLEX.GET_NUMBER_OF_ORDERS_OTOFLEX,
		body,
		parseData: afterFetch
	});
	return (
		<Card ref={ref}>
			<ChartHeader
				title={'numberOfOrders'}
				filter={<DateFilter defaultDay={'last7Days'} setValue={setFilter} />}
			/>
			<AreaChartV2 data={data} loading={loading} />
		</Card>
	);
};

const DriverPerformance = () => {
	const [filter, setFilter] = useState({
		start: dayjs().subtract(7, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end }), [filter]);

	const driverPerformanceColumns = useMemo(
		() => [
			{
				Header: 'driverName',
				accessor: 'driverName',
				width: 100,
				filterType: FilterType.string,
				showOnMobile: true
			},
			{
				Header: 'deliveryTime',
				accessor: 'deliveryTime',
				width: 100,
				filterType: FilterType.string
			},
			{ Header: 'pickupTime', accessor: 'pickupTime', width: 100, filterType: FilterType.string },
			{ Header: 'delivered', accessor: 'delivered', width: 100, filterType: FilterType.string },
			{ Header: 'returned', accessor: 'returned', width: 100, filterType: FilterType.string },
			{ Header: 'undelivered', accessor: 'undelivered', width: 100, filterType: FilterType.string },
			{ Header: 'totalAssigned', accessor: 'totalAssigned', width: 100, filterType: FilterType.string },
			{ Header: 'deliveryRate', accessor: 'deliveryRate', width: 100, filterType: FilterType.number },
			{ Header: 'returnRate', accessor: 'returnRate', width: 100, filterType: FilterType.number }
		],
		[]
	);

	const defaultFilters = useMemo(
		() => [
			...(body
				? [
						{
							column: {
								Header: 'createdAt',
								accessor: 'createdAt',
								filterType: FilterType.date
							},
							action: 'between',
							value: {
								start: body.from,
								end: body.to
							}
						}
					]
				: [])
		],
		[body]
	);

	return (
		<Card>
			<ChartHeader
				title={'driverPerformance'}
				filter={<DateFilter defaultDay={'last7Days'} setValue={setFilter} />}
			/>
			<DynamicListChartContainer>
				<DynamicList
					id={DYNAMICLISTS.DRIVER_PERFORMANCE}
					columns={driverPerformanceColumns}
					exportName="driverPerformance"
					fetchUrl={REPORT.OTO_FLEX.GET_DRIVER_PERFORMANCE}
					defaultFilters={defaultFilters}
				/>
			</DynamicListChartContainer>
		</Card>
	);
};

const OrderValuesSummary = () => {
	const cardsConfig = [
		{
			accessor: 'deliveryRate',
			label: 'deliveryRate',
			tooltip: 'dashboard.deliveryRate.description',
			customIcon: 'fa-regular-wave-pulse-circle-bolt',
			color: 'var(--fa-regular-wave-pulse-circle-bolt-color)',
			backgroundColor: 'var(--fa-regular-wave-pulse-circle-bolt-bgColor)'
		},
		{
			accessor: 'failedDeliveryAttempt',
			label: 'failedDeliveryAttempt',
			tooltip: 'dashboard.failedDeliveryAttempt.description',
			customIcon: 'fa-regular-box-circle-xmark',
			backgroundColor: 'var(--fa-regular-box-circle-xmark-bgColor)',
			color: 'var(--fa-regular-box-circle-xmark-color)'
		},
		{
			accessor: 'processingOrders',
			label: 'processingOrders',
			tooltip: 'dashboard.processingOrders.description',
			customIcon: 'fa-regular-loader-circle-bolt',
			backgroundColor: 'var(--fa-regular-loader-circle-bolt-bgColor)',
			color: 'var(--fa-regular-loader-circle-bolt-color)'
		},
		{
			accessor: 'returnRate',
			label: 'returnRate',
			tooltip: 'dashboard.returnRate.description',
			customIcon: 'fa-regular-wave-pulse-circle-arrow-left',
			backgroundColor: 'var(--fa-regular-wave-pulse-circle-arrow-left-bgColor)',
			color: 'var(--fa-regular-wave-pulse-circle-arrow-left-color)'
		},
		{
			accessor: 'totalCODCollected',
			label: 'totalCODCollected',
			tooltip: 'dashboard.totalCODCollected.description',
			customIcon: 'fa-regular-money-bill-1-wave-circle-check',
			backgroundColor: 'var(--fa-regular-money-bill-1-wave-circle-check-bgColor)',
			color: 'var(--fa-regular-money-bill-1-wave-circle-check-color)'
		},
		{
			accessor: 'totalDelivered',
			label: 'totalDelivered',
			tooltip: 'dashboard.totalDelivered.description',
			customIcon: 'fa-regular-box-circle-check',
			backgroundColor: 'var(--fa-regular-box-circle-check-bgColor)',
			color: 'var(--fa-regular-box-circle-check-color)'
		},
		{
			accessor: 'totalOrders',
			label: 'totalOrders',
			tooltip: 'dashboard.totalOrders.description',
			customIcon: 'fa-regular-boxes-stacked-circle-check',
			backgroundColor: 'var(--fa-regular-boxes-stacked-circle-check-bgColor)',
			color: 'var(--fa-regular-boxes-stacked-circle-check-color)'
		},
		{
			accessor: 'totalReturned',
			label: 'totalReturned',
			tooltip: 'dashboard.totalReturned.description',
			customIcon: 'fa-regular-box-circle-arrow-left',
			backgroundColor: 'var(--fa-regular-box-circle-arrow-left-bgColor)',
			color: 'var(--fa-regular-box-circle-arrow-left-color)'
		},
		{
			accessor: 'avgDeliveryTime',
			label: 'avgDeliveryTime',
			tooltip: 'dashboard.avgDeliveryTime.description',
			customIcon: 'fa-regular-wave-pulse-circle-arrow-left',
			backgroundColor: 'var(--fa-regular-wave-pulse-circle-arrow-left-bgColor)',
			color: 'var(--fa-regular-wave-pulse-circle-arrow-left-color)'
		}
	];

	const body = useMemo(() => ({ from: null, to: null }), []);
	const { ref, data, loading } = useFetchAndParseOnView({ url: REPORT.OTO_FLEX.GET_ALL_ORDERS_SUMMARY, body });

	return (
		<div ref={ref}>
			<ChartHeader title={'orderValuesSummary'} />
			<DataCards data={data} loading={loading} cardsConfig={cardsConfig} />
		</div>
	);
};

export default OTOFlexTab;
